import { useSyncExternalStore } from 'react';

/* estilos-1 (Bloque C, #769 a #780): LOS ESTILOS DE EXAMEN EN LA SESION, calcado de `tutorDeLaSesion.js`.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   `{listo, on, preferido, categorias, estilos}`. El Panel (al entrar con sesion) y Ajustes (al abrirse) llaman
   `cargarEstilosSiFalta(user)`: UNA llamada a `GET /estilos` por sesion; el resultado se guarda en memoria. Con la
   perilla apagada (o sin respuesta) `on` es falso y NADA de los estilos se pinta ni viaja: `cuerpoDeEstilo()` da `{}`
   y las cuatro subidas salen byte a byte como antes.

   El valor de la pastilla ES `preferido` (null = Quizessa): "Listo" lo cambia aqui (`guardarPreferido`) y lo guarda
   con `PUT /estilos/preferido`; la pastilla, Ajustes y las subidas lo leen de aqui. Si el PUT falla, la sesion se
   queda con lo elegido (las subidas lo mandan igual) y el servidor conserva el preferido de antes.

   EL CLIENTE SE PIDE AL LLAMAR, NO ARRIBA (la mina de la tanda 28b: un `import` de `./api` arrastra `src/i18n.js`). */

function cliente() { return require('./api').default; }

export const QUIZESSA = 'quizessa';

const VACIO = Object.freeze({ listo: false, on: false, preferido: null, categorias: [], estilos: [] });

let estado = VACIO;
let dueno;
let enVuelo = null;
let generacion = 0;
const oyentes = new Set();

function avisar() {
  oyentes.forEach((fn) => fn());
}

function suscribir(fn) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

function leer() {
  return estado;
}

const porOrden = (a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0);

/** Lo que contesto el servidor, saneado. Sin `on: true` o sin un solo examen en el catalogo: apagado. */
export function sanearEstilos(datos) {
  const d = datos && typeof datos === 'object' ? datos : {};
  const estilos = Array.isArray(d.estilos)
    ? d.estilos.filter((e) => e && typeof e.clave === 'string' && e.clave) : [];
  const hayExamen = estilos.some((e) => e.clave !== QUIZESSA);
  if (d.on !== true || !hayExamen) {
    return Object.freeze({ listo: true, on: false, preferido: null, categorias: [], estilos: [] });
  }
  const categorias = (Array.isArray(d.categorias) ? d.categorias : [])
    .filter((c) => c && typeof c.clave === 'string').slice().sort(porOrden);
  const preferido = typeof d.preferido === 'string' && d.preferido !== QUIZESSA
    && estilos.some((e) => e.clave === d.preferido) ? d.preferido : null;
  return Object.freeze({ listo: true, on: true, preferido, categorias, estilos });
}

/** Lo que leen la pastilla, la hoja, Ajustes y el renglon del tipo. */
export function useEstilos() {
  return useSyncExternalStore(suscribir, leer, leer);
}

/** Sin suscribirse (las subidas, pruebas). */
export function estilosActuales() {
  return estado;
}

function asegurarDueno(idDelUsuario) {
  const id = idDelUsuario ?? null;
  if (dueno !== undefined && dueno !== id) olvidarEstilos();
  dueno = id;
}

/** Publica lo que contesto `GET /estilos`. */
export function publicarEstilos(datos) {
  estado = sanearEstilos(datos);
  avisar();
}

/** UNA llamada por sesion: con dato o con una en vuelo no pide nada. Un fallo no se guarda (la siguiente entrada
 *  vuelve a preguntar) y deja todo apagado. */
export function cargarEstilosSiFalta(user) {
  if (!user) return Promise.resolve(null);
  asegurarDueno(user.id);
  if (estado.listo) return Promise.resolve(estado);
  if (enVuelo) return enVuelo;
  const miGeneracion = generacion;
  let promesa;
  try {
    promesa = cliente().get('/estilos');
  } catch (e) {
    return Promise.resolve(null);
  }
  enVuelo = Promise.resolve(promesa)
    .then((res) => {
      if (miGeneracion !== generacion) return null;
      if (!res || !res.data || typeof res.data !== 'object') return null;
      publicarEstilos(res.data);
      return estado;
    })
    .catch(() => null)
    .finally(() => {
      if (miGeneracion === generacion) enVuelo = null;
    });
  return enVuelo;
}

/** "Listo": la pastilla y Ajustes cambian al instante y el servidor lo recuerda para las proximas subidas. */
export function guardarPreferido(clave) {
  if (!estado.on) return Promise.resolve(null);
  const valida = typeof clave === 'string' && estado.estilos.some((e) => e.clave === clave);
  if (!valida) return Promise.resolve(null);
  const preferido = clave === QUIZESSA ? null : clave;
  if (preferido !== estado.preferido) {
    estado = Object.freeze({ ...estado, preferido });
    avisar();
  }
  let promesa;
  try {
    promesa = cliente().put('/estilos/preferido', { estilo: clave });
  } catch (e) {
    return Promise.resolve(null);
  }
  return Promise.resolve(promesa).then((res) => (res && res.data) || null).catch(() => null);
}

/** Lo que las cuatro subidas suman a su cuerpo: `{estilo}` con la perilla encendida; `{}` con ella apagada. */
export function cuerpoDeEstilo() {
  if (!estado.on) return {};
  return { estilo: estado.preferido || QUIZESSA };
}

/** Al cerrar sesion o cambiar de usuario. */
export function olvidarEstilos() {
  generacion += 1;
  enVuelo = null;
  dueno = undefined;
  if (estado === VACIO) return;
  estado = VACIO;
  avisar();
}

/** Solo pruebas. */
export function __reiniciarEstilosParaPruebas() {
  generacion += 1;
  enVuelo = null;
  dueno = undefined;
  estado = VACIO;
}

// -- Lecturas del catalogo ---------------------------------------------------------------------

/** "es" o "en": el idioma de la app. */
export function idiomaDe(lengua) {
  return typeof lengua === 'string' && lengua.toLowerCase().startsWith('es') ? 'es' : 'en';
}

export function estiloPorClave(e, clave) {
  if (!e || !Array.isArray(e.estilos) || typeof clave !== 'string') return null;
  return e.estilos.find((x) => x.clave === clave) || null;
}

/** El nombre largo en el idioma de la app ("NCLEX", "USMLE Step 2 CK", "Quizessa + Pensamiento critico"). */
export function nombreLargo(e, clave, idioma) {
  const est = estiloPorClave(e, clave || QUIZESSA);
  if (!est) return '';
  const largo = est.nombre_largo && typeof est.nombre_largo === 'object' ? est.nombre_largo : {};
  return largo[idiomaDe(idioma)] || largo.en || largo.es || est.nombre_corto || '';
}

/** El nombre corto del examen de un documento, para el renglon del tipo; null sin perilla, sin estilo o con uno que
 *  el catalogo no conoce (entonces el renglon es el de hoy). */
export function nombreCortoDelDocumento(e, doc) {
  if (!e || !e.on || !doc || typeof doc.estilo !== 'string' || doc.estilo === QUIZESSA) return null;
  const est = estiloPorClave(e, doc.estilo);
  return est && typeof est.nombre_corto === 'string' && est.nombre_corto ? est.nombre_corto : null;
}

/** Sin acentos, sin mayusculas y con todo lo que no es letra o numero como espacio. */
export function normalizar(texto) {
  return String(texto == null ? '' : texto)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/** El buscador: lo escrito tiene que empezar una palabra del nombre largo (los dos idiomas), del corto o de un alias. */
export function coincide(est, consulta) {
  const q = normalizar(consulta);
  if (!q) return true;
  if (!est) return false;
  const largo = est.nombre_largo && typeof est.nombre_largo === 'object' ? Object.values(est.nombre_largo) : [];
  const campos = [...largo, est.nombre_corto, ...(Array.isArray(est.alias) ? est.alias : [])];
  return campos.some((c) => ` ${normalizar(c)}`.includes(` ${q}`));
}

/** Las categorias en el orden del catalogo, cada una con sus examenes en orden. */
export function examenesPorCategoria(e) {
  if (!e || !e.on) return [];
  return e.categorias.map((c) => ({
    clave: c.clave,
    examenes: e.estilos.filter((x) => x.categoria === c.clave && x.clave !== QUIZESSA).slice().sort(porOrden),
  })).filter((c) => c.examenes.length > 0);
}

/** La lista plana del buscador, en el orden del catalogo. */
export function examenesQueCoinciden(e, consulta) {
  return examenesPorCategoria(e).flatMap((c) => c.examenes.filter((x) => coincide(x, consulta)));
}
